import React, { useEffect, useMemo, useRef, useState } from "react";

export default function KeyStageMultiSelect({
  selectedGroup = "",
  onChange,
  groupOptions = [],
  isTailorFeedEnabled = false,
  schoolGroup,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Filter options based on schoolGroup permission and include id 18 (Post-18)
  const filteredOptions = useMemo(() => {
    return (groupOptions || []).filter((ele) => {
      return !schoolGroup || schoolGroup.includes(ele.id) || ele.id === 18;
    });
  }, [groupOptions, schoolGroup]);

  // Parse comma-separated or array of selected IDs as strings
  const selectedList = useMemo(() => {
    if (!selectedGroup) return [];
    if (Array.isArray(selectedGroup)) {
      return selectedGroup.map(String).filter(Boolean);
    }
    return String(selectedGroup)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [selectedGroup]);

  // Click outside listener to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Compute trigger button label
  const buttonLabel = useMemo(() => {
    if (selectedList.length === 0) {
      return isTailorFeedEnabled
        ? "Tailored Feed On"
        : "Filter content by Key Stage";
    }

    const selectedNames = selectedList
      .map((id) => {
        const found = filteredOptions.find((g) => String(g.id) === String(id));
        return found ? found.name : `Group ${id}`;
      })
      .filter(Boolean);

    if (selectedNames.length === 1) {
      return selectedNames[0];
    }

    const joined = selectedNames.join(", ");
    if (joined.length <= 22) {
      return joined;
    }
    return `${selectedNames.length} Key Stages`;
  }, [selectedList, filteredOptions, isTailorFeedEnabled]);

  const toggleOption = (id) => {
    const strId = String(id);
    let next;
    if (selectedList.includes(strId)) {
      next = selectedList.filter((item) => item !== strId);
    } else {
      next = [...selectedList, strId];
    }
    if (onChange) {
      onChange(next);
    }
  };

  const handleClearAll = () => {
    if (onChange) {
      onChange([]);
    }
  };

  return (
    <div className="custom-select-wrapper" ref={containerRef}>
      <div
        className="cmn-select ks-select-trigger"
        onClick={() => setIsOpen((prev) => !prev)}
        role="button"
        tabIndex={0}
        title={buttonLabel}
        aria-expanded={isOpen}
      >
        <span className="ks-select-trigger-text">{buttonLabel}</span>
      </div>

      {isOpen && (
        <div className="ks-dropdown-menu">
          {/* Header row: Filter content by Key Stage */}
          <div
            className={`ks-dropdown-item ks-all-option${
              selectedList.length === 0 ? " active-all" : ""
            }`}
            onClick={handleClearAll}
          >
            <span>
              {isTailorFeedEnabled
                ? "Tailored Feed On"
                : "Filter content by Key Stage"}
            </span>
          </div>

          {/* Key Stage rows with toggle switch */}
          {filteredOptions.map((opt) => {
            const isChecked = selectedList.includes(String(opt.id));
            return (
              <div
                key={opt.id}
                className="ks-dropdown-item ks-option-row"
                onClick={() => toggleOption(opt.id)}
              >
                <span className="ks-opt-text">{opt.name}</span>
                <div
                  className={`ks-toggle-switch${isChecked ? " on" : ""}`}
                  role="switch"
                  aria-checked={isChecked}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOption(opt.id);
                  }}
                >
                  <span className="ks-toggle-thumb" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
